// Procedural Web Audio synth for Cosmic Par. Zero assets — every sound is
// built from oscillators + noise + filters + envelopes so the bundle stays
// small. Lazy-initialized on the first user gesture (iOS requirement).

const MUTE_KEY = "cosmicpar.mute.v1";
const VOLUME_KEY = "cosmicpar.volume.v1";
const DEFAULT_VOLUME = 0.7;

// ---------- core ----------

interface Engine {
  ctx: AudioContext;
  master: GainNode;
  comp: DynamicsCompressorNode;
}

let engine: Engine | null = null;
let unlocked = false;
let muted = readMuted();
let volume = readVolume();
const muteListeners = new Set<(muted: boolean) => void>();
const volumeListeners = new Set<(volume: number) => void>();

function readMuted(): boolean {
  try {
    return localStorage.getItem(MUTE_KEY) === "1";
  } catch {
    return false;
  }
}

function writeMuted(v: boolean): void {
  try {
    localStorage.setItem(MUTE_KEY, v ? "1" : "0");
  } catch {
    // ignore
  }
}

function readVolume(): number {
  try {
    const raw = localStorage.getItem(VOLUME_KEY);
    if (raw == null) return DEFAULT_VOLUME;
    const n = Number(raw);
    if (!Number.isFinite(n)) return DEFAULT_VOLUME;
    return Math.min(1, Math.max(0, n));
  } catch {
    return DEFAULT_VOLUME;
  }
}

function writeVolume(v: number): void {
  try {
    localStorage.setItem(VOLUME_KEY, String(v));
  } catch {
    // ignore
  }
}

function targetGain(): number {
  return muted ? 0 : volume;
}

function ensureEngine(): Engine | null {
  if (engine) return engine;
  try {
    const Ctx =
      (window as unknown as { AudioContext?: typeof AudioContext })
        .AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!Ctx) return null;
    const ctx = new Ctx();
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -14;
    comp.knee.value = 20;
    comp.ratio.value = 4;
    comp.attack.value = 0.003;
    comp.release.value = 0.12;
    const master = ctx.createGain();
    master.gain.value = targetGain();
    comp.connect(master);
    master.connect(ctx.destination);
    engine = { ctx, master, comp };
    return engine;
  } catch {
    return null;
  }
}

/**
 * Must be called inside a user-gesture handler (click/touch/keydown). iOS
 * Safari keeps the AudioContext suspended until a trusted gesture resumes it.
 */
export function unlockAudio(): void {
  const e = ensureEngine();
  if (!e) return;
  if (unlocked) return;
  if (e.ctx.state === "suspended") {
    void e.ctx.resume().catch(() => void 0);
  }
  // Play a tiny silent tick so iOS is definitely happy.
  const osc = e.ctx.createOscillator();
  const g = e.ctx.createGain();
  g.gain.value = 0;
  osc.connect(g).connect(e.comp);
  osc.start();
  osc.stop(e.ctx.currentTime + 0.01);
  unlocked = true;
}

export function isMuted(): boolean {
  return muted;
}

export function setMuted(v: boolean): void {
  muted = v;
  writeMuted(v);
  const e = engine;
  if (e) {
    const now = e.ctx.currentTime;
    e.master.gain.cancelScheduledValues(now);
    e.master.gain.linearRampToValueAtTime(targetGain(), now + 0.08);
  }
  for (const cb of muteListeners) cb(v);
}

export function onMuteChange(cb: (muted: boolean) => void): () => void {
  muteListeners.add(cb);
  return () => muteListeners.delete(cb);
}

export function getVolume(): number {
  return volume;
}

export function setVolume(v: number): void {
  volume = Math.min(1, Math.max(0, v));
  writeVolume(volume);
  const e = engine;
  if (e) {
    const now = e.ctx.currentTime;
    e.master.gain.cancelScheduledValues(now);
    e.master.gain.linearRampToValueAtTime(targetGain(), now + 0.06);
  }
  for (const cb of volumeListeners) cb(volume);
}

export function onVolumeChange(cb: (volume: number) => void): () => void {
  volumeListeners.add(cb);
  return () => volumeListeners.delete(cb);
}

// ---------- helpers ----------

function envGain(
  ctx: AudioContext,
  peak: number,
  attack: number,
  decay: number,
  startAt = ctx.currentTime,
): GainNode {
  const g = ctx.createGain();
  g.gain.setValueAtTime(0, startAt);
  g.gain.linearRampToValueAtTime(peak, startAt + attack);
  g.gain.exponentialRampToValueAtTime(
    Math.max(peak * 0.0005, 1e-4),
    startAt + attack + decay,
  );
  return g;
}

function noiseBuffer(ctx: AudioContext, seconds: number): AudioBuffer {
  const rate = ctx.sampleRate;
  const len = Math.max(1, Math.floor(rate * seconds));
  const buf = ctx.createBuffer(1, len, rate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
  return buf;
}

// Procedural stereo IR — exponentially-decaying white noise. Used by the
// ConvolverNode for a cheap "small cathedral" reverb tail. Generated lazily
// and cached per engine.
let cachedIR: AudioBuffer | null = null;
function impulseResponse(
  ctx: AudioContext,
  seconds = 1.8,
  decay = 3.2,
): AudioBuffer {
  if (cachedIR && cachedIR.sampleRate === ctx.sampleRate) return cachedIR;
  const rate = ctx.sampleRate;
  const len = Math.max(1, Math.floor(rate * seconds));
  const buf = ctx.createBuffer(2, len, rate);
  for (let ch = 0; ch < 2; ch++) {
    const data = buf.getChannelData(ch);
    for (let i = 0; i < len; i++) {
      const t = i / len;
      // Small predelay pocket at the head so the direct signal isn't smeared.
      const pre = i < rate * 0.01 ? 0 : 1;
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - t, decay) * pre;
    }
  }
  cachedIR = buf;
  return buf;
}

/** Build a reverb send: input -> convolver -> wet gain -> dest. Returns the
 *  input node to connect sources into, plus a dispose() for cleanup. */
function reverbSend(
  ctx: AudioContext,
  dest: AudioNode,
  wet = 0.35,
): GainNode {
  const input = ctx.createGain();
  input.gain.value = 1;
  const conv = ctx.createConvolver();
  conv.buffer = impulseResponse(ctx);
  const wetGain = ctx.createGain();
  wetGain.gain.value = wet;
  // Soften the tail a hair so it reads "distant" not "splashy".
  const tameHi = ctx.createBiquadFilter();
  tameHi.type = "lowpass";
  tameHi.frequency.value = 4200;
  tameHi.Q.value = 0.5;
  input.connect(conv).connect(tameHi).connect(wetGain).connect(dest);
  return input;
}

// ---------- SFX ----------

export const sfx = {
  shot(): void {
    const e = ensureEngine();
    if (!e) return;
    const { ctx, comp } = e;
    const t = ctx.currentTime;

    // Noise whoosh through a sweeping bandpass.
    const src = ctx.createBufferSource();
    src.buffer = noiseBuffer(ctx, 0.3);
    const bp = ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.Q.value = 6;
    bp.frequency.setValueAtTime(180, t);
    bp.frequency.exponentialRampToValueAtTime(1200, t + 0.22);
    const g = envGain(ctx, 0.25, 0.004, 0.3, t);
    src.connect(bp).connect(g).connect(comp);
    src.start(t);
    src.stop(t + 0.32);

    // Sub body — saw sweeping down for thump.
    const sub = ctx.createOscillator();
    sub.type = "sawtooth";
    sub.frequency.setValueAtTime(120, t);
    sub.frequency.exponentialRampToValueAtTime(40, t + 0.25);
    const subG = envGain(ctx, 0.28, 0.005, 0.28, t);
    sub.connect(subG).connect(comp);
    sub.start(t);
    sub.stop(t + 0.3);
  },

  collide(intensity = 1): void {
    const e = ensureEngine();
    if (!e) return;
    const { ctx, comp } = e;
    const t = ctx.currentTime;
    // Softer peak overall; scales with intensity so heavy hits still register.
    const peak = Math.min(0.45, 0.2 * intensity);

    // Low thud — slower attack, longer decay, heavier low-pass for a muffled
    // "through space" feel rather than a snare snap.
    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(80, t);
    osc.frequency.exponentialRampToValueAtTime(38, t + 0.18);
    const thudLp = ctx.createBiquadFilter();
    thudLp.type = "lowpass";
    thudLp.frequency.value = 180;
    thudLp.Q.value = 0.6;
    const g = envGain(ctx, peak, 0.018, 0.38, t);
    osc.connect(thudLp).connect(g).connect(comp);
    osc.start(t);
    osc.stop(t + 0.45);

    // Muffled noise body — much darker bandpass centered lower, softer
    // attack so the transient isn't a clicky tick.
    const src = ctx.createBufferSource();
    src.buffer = noiseBuffer(ctx, 0.22);
    const bp = ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.frequency.value = 220;
    bp.Q.value = 1.6;
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 500;
    lp.Q.value = 0.5;
    const ng = envGain(ctx, 0.07 * intensity, 0.012, 0.26, t);
    src.connect(bp).connect(lp).connect(ng).connect(comp);
    src.start(t);
    src.stop(t + 0.28);
  },

  oob(): void {
    const e = ensureEngine();
    if (!e) return;
    const { ctx, comp } = e;
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(520, t);
    osc.frequency.exponentialRampToValueAtTime(90, t + 0.45);
    const g = envGain(ctx, 0.22, 0.005, 0.5, t);
    osc.connect(g).connect(comp);
    osc.start(t);
    osc.stop(t + 0.55);

    // Pitched noise underneath.
    const src = ctx.createBufferSource();
    src.buffer = noiseBuffer(ctx, 0.45);
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.setValueAtTime(800, t);
    lp.frequency.exponentialRampToValueAtTime(150, t + 0.45);
    const ng = envGain(ctx, 0.08, 0.006, 0.45, t);
    src.connect(lp).connect(ng).connect(comp);
    src.start(t);
    src.stop(t + 0.5);
  },

  hole(delta: number): void {
    const e = ensureEngine();
    if (!e) return;
    const { ctx, comp } = e;
    const t = ctx.currentTime;
    // Subtle reverb send so the chime rings out a bit — "distant cathedral".
    const wet = reverbSend(ctx, comp, 0.28);
    // Chord based on performance: bigger when under par.
    const chords = chordFor(delta);
    chords.forEach((hz, i) => {
      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.value = hz;
      const start = t + i * 0.06;
      const g = envGain(ctx, 0.18, 0.01, 0.65, start);
      // Light detune for chorus.
      const osc2 = ctx.createOscillator();
      osc2.type = "triangle";
      osc2.frequency.value = hz * 1.004;
      const g2 = envGain(ctx, 0.12, 0.01, 0.55, start);
      // Dry: straight to comp. Wet: through reverb send.
      osc.connect(g);
      osc2.connect(g2);
      g.connect(comp);
      g2.connect(comp);
      g.connect(wet);
      g2.connect(wet);
      osc.start(start);
      osc2.start(start);
      osc.stop(start + 0.75);
      osc2.stop(start + 0.65);
    });
  },

  holeInOne(): void {
    this.hole(-99);
    // Shimmer on top.
    const e = ensureEngine();
    if (!e) return;
    const { ctx, comp } = e;
    const t = ctx.currentTime;
    for (let i = 0; i < 8; i++) {
      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.value = 880 * Math.pow(1.25, i % 4);
      const start = t + 0.3 + i * 0.07;
      const g = envGain(ctx, 0.08, 0.008, 0.25, start);
      osc.connect(g).connect(comp);
      osc.start(start);
      osc.stop(start + 0.3);
    }
  },

  uiClick(): void {
    const e = ensureEngine();
    if (!e) return;
    const { ctx, comp } = e;
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(920, t);
    osc.frequency.exponentialRampToValueAtTime(720, t + 0.05);
    const g = envGain(ctx, 0.12, 0.002, 0.08, t);
    osc.connect(g).connect(comp);
    osc.start(t);
    osc.stop(t + 0.1);
  },

  /** Triumphant ~1.8s major-key progression played after the 9th hole.
   *  Three chord stabs ascending (IV → V → I) with a sparkle tail on top. */
  winFanfare(): void {
    const e = ensureEngine();
    if (!e) return;
    const { ctx, comp } = e;
    const t = ctx.currentTime;

    // Shared reverb send — makes the whole flourish feel like a space.
    const wet = reverbSend(ctx, comp, 0.3);

    // C major: IV (F/A/C), V (G/B/D), I (C/E/G) — each one step brighter.
    const chords: number[][] = [
      [349.23, 440.0, 523.25], // F major
      [392.0, 493.88, 587.33], // G major
      [523.25, 659.25, 783.99, 1046.5], // C major + octave
    ];
    const stride = 0.34;

    chords.forEach((chord, ci) => {
      const start = t + ci * stride;
      // Extra push on the final chord.
      const velocity = ci === chords.length - 1 ? 1.25 : 1.0;
      chord.forEach((hz, i) => {
        // Detuned saw + triangle pair = warm but lively.
        const saw = ctx.createOscillator();
        saw.type = "sawtooth";
        saw.frequency.value = hz;
        const tri = ctx.createOscillator();
        tri.type = "triangle";
        tri.frequency.value = hz * 1.004;

        // Gentle per-chord lowpass so the saws don't buzz.
        const tone = ctx.createBiquadFilter();
        tone.type = "lowpass";
        tone.frequency.setValueAtTime(1200, start);
        tone.frequency.linearRampToValueAtTime(3200, start + 0.12);
        tone.Q.value = 0.4;

        const voiceOffset = i * 0.008; // tiny voice-stagger for life
        const gSaw = envGain(
          ctx,
          0.09 * velocity,
          0.012,
          ci === chords.length - 1 ? 0.9 : 0.3,
          start + voiceOffset,
        );
        const gTri = envGain(
          ctx,
          0.11 * velocity,
          0.012,
          ci === chords.length - 1 ? 0.9 : 0.32,
          start + voiceOffset,
        );
        saw.connect(tone);
        tri.connect(tone);
        tone.connect(gSaw);
        tone.connect(gTri);
        gSaw.connect(comp);
        gTri.connect(comp);
        gSaw.connect(wet);
        gTri.connect(wet);
        saw.start(start + voiceOffset);
        tri.start(start + voiceOffset);
        const stopAt = start + voiceOffset + (ci === chords.length - 1 ? 1.1 : 0.42);
        saw.stop(stopAt);
        tri.stop(stopAt);
      });
    });

    // Bass reinforcement under the final tonic.
    const finalStart = t + (chords.length - 1) * stride;
    const bass = ctx.createOscillator();
    bass.type = "sine";
    bass.frequency.setValueAtTime(130.81, finalStart); // C3
    bass.frequency.linearRampToValueAtTime(261.63, finalStart + 0.05); // C4 punch
    const bassG = envGain(ctx, 0.22, 0.005, 0.8, finalStart);
    bass.connect(bassG).connect(comp);
    bass.start(finalStart);
    bass.stop(finalStart + 0.95);

    // Shimmer layer — ascending sine bells on top of the tonic.
    const shimmerBase = t + 0.18;
    const shimmerNotes = [1046.5, 1318.51, 1567.98, 2093.0, 2637.02];
    shimmerNotes.forEach((hz, i) => {
      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.value = hz;
      const start = shimmerBase + i * 0.09;
      const g = envGain(ctx, 0.06, 0.008, 0.5, start);
      osc.connect(g);
      g.connect(comp);
      g.connect(wet);
      osc.start(start);
      osc.stop(start + 0.55);
    });
  },
};

function chordFor(delta: number): number[] {
  if (delta <= -2) {
    // Triumphant: major 7 up high.
    return [523.25, 659.25, 783.99, 987.77, 1174.66];
  }
  if (delta <= 0) {
    // Par or birdie: major triad.
    return [523.25, 659.25, 783.99, 987.77];
  }
  if (delta <= 2) {
    // Bogey: minor 7.
    return [440, 523.25, 659.25, 783.99];
  }
  // Worse: sus4, slightly sour.
  return [392, 523.25, 587.33];
}

// ---------- Charge tone (held while aiming) ----------
//
// Warm soft "tension pull" — two detuned triangle waves through a
// dampened lowpass + slow LFO tremolo. Reads like a rubber-band
// stretching rather than a drone or a hiss. Fundamental climbs
// slightly with power (root stays under 300 Hz so it never cuts)
// and the lowpass opens just a touch so the harmonics bloom on a
// full-power draw. Gain caps at ~0.02 master.

interface ChargeHandle {
  update(power: number): void;
  release(): void;
}

export function startCharge(): ChargeHandle | null {
  const e = ensureEngine();
  if (!e || muted) {
    return {
      update: () => void 0,
      release: () => void 0,
    };
  }
  const { ctx, comp } = e;
  const t = ctx.currentTime;

  const rootHz = 165; // E3 — low enough to feel warm, high enough to track
  const a = ctx.createOscillator();
  const b = ctx.createOscillator();
  a.type = "triangle";
  b.type = "triangle";
  a.frequency.value = rootHz;
  b.frequency.value = rootHz * 1.0035; // very slight detune → chorus

  const lp = ctx.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.value = 900;
  lp.Q.value = 0.6;

  const g = ctx.createGain();
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(0.009, t + 0.15);

  // Slow tremolo on the gain for a breathing feel.
  const lfo = ctx.createOscillator();
  lfo.frequency.value = 0.8;
  const lfoAmt = ctx.createGain();
  lfoAmt.gain.value = 0.003; // tiny swing, ± ~0.003 on top of base gain
  lfo.connect(lfoAmt).connect(g.gain);
  lfo.start(t);

  a.connect(lp);
  b.connect(lp);
  lp.connect(g).connect(comp);
  a.start(t);
  b.start(t);

  let released = false;

  return {
    update(power: number) {
      if (released || !engine) return;
      const now = engine.ctx.currentTime;
      const p = Math.max(0, Math.min(1, power));
      // Pitch climbs gently: 165 → 247 Hz (about a fifth). Feels like
      // tension, not alarm.
      const pitch = rootHz + 82 * p;
      a.frequency.setTargetAtTime(pitch, now, 0.08);
      b.frequency.setTargetAtTime(pitch * 1.0035, now, 0.08);
      // Cutoff opens so the top end sparkles on a full draw.
      lp.frequency.setTargetAtTime(900 + 1600 * p, now, 0.1);
      // Volume creeps up but stays under 0.022 peak.
      g.gain.setTargetAtTime(0.009 + 0.013 * p, now, 0.1);
    },
    release() {
      if (released || !engine) return;
      released = true;
      const now = engine.ctx.currentTime;
      g.gain.cancelScheduledValues(now);
      g.gain.setValueAtTime(g.gain.value, now);
      g.gain.exponentialRampToValueAtTime(1e-4, now + 0.18);
      a.stop(now + 0.22);
      b.stop(now + 0.22);
      lfo.stop(now + 0.22);
    },
  };
}

// ---------- Ambient drone (optional, very low) ----------

let ambientHandle: { stop: () => void } | null = null;

export function startAmbient(): void {
  if (ambientHandle) return;
  const e = ensureEngine();
  if (!e) return;
  const { ctx, comp } = e;
  const t = ctx.currentTime;

  // Two low pad sines (octave + fifth-ish interval).
  const pads: OscillatorNode[] = [];
  const freqs = [55, 82.4]; // A1, E2
  const lp = ctx.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.value = 700;
  const g = ctx.createGain();
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(0.05, t + 3);
  // LFO on filter freq for breathing.
  const lfo = ctx.createOscillator();
  lfo.frequency.value = 0.08;
  const lfoAmt = ctx.createGain();
  lfoAmt.gain.value = 220;
  lfo.connect(lfoAmt).connect(lp.frequency);
  lfo.start(t);

  for (const f of freqs) {
    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.value = f;
    osc.connect(lp);
    osc.start(t);
    pads.push(osc);
    // Detuned partner.
    const det = ctx.createOscillator();
    det.type = "triangle";
    det.frequency.value = f * 1.503; // fifth up
    const dGain = ctx.createGain();
    dGain.gain.value = 0.3;
    det.connect(dGain).connect(lp);
    det.start(t);
    pads.push(det);
  }
  lp.connect(g).connect(comp);

  ambientHandle = {
    stop() {
      const now = e.ctx.currentTime;
      g.gain.cancelScheduledValues(now);
      g.gain.setValueAtTime(g.gain.value, now);
      g.gain.exponentialRampToValueAtTime(1e-4, now + 1.2);
      for (const o of pads) o.stop(now + 1.3);
      lfo.stop(now + 1.3);
    },
  };

  // Kick off the space-beats loop alongside the pad so the soundscape
  // has motion. Auto-stops when the pad stops (via stopAmbient).
  startBeats();
}

export function stopAmbient(): void {
  ambientHandle?.stop();
  ambientHandle = null;
  stopBeats();
}

// ---------- Space background beats ----------
//
// A slow, sparse beat loop that sits under the ambient pad so the
// game feels inhabited instead of cold-silent. 8-step pattern at
// 78 BPM: soft kick on 1/5, ghost hat on odd eighths, a tinkling
// bell on beat 3. Everything is procedurally synthesized — no asset
// bytes, no network fetch — and the master gain caps at ~0.08 so it
// never fights with the pad or SFX. Started automatically by
// `startAmbient` when the user unlocks audio.

let beatsHandle: { stop: () => void } | null = null;

export function startBeats(): void {
  if (beatsHandle) return;
  const e = ensureEngine();
  if (!e) return;
  const { ctx, comp } = e;

  const master = ctx.createGain();
  master.gain.value = 0;
  master.connect(comp);
  master.gain.setValueAtTime(0, ctx.currentTime);
  master.gain.linearRampToValueAtTime(0.055, ctx.currentTime + 2.0);

  const stepSec = 60 / 78 / 2; // 8th-notes at 78 BPM (~0.385s)
  const patternLen = 16; // two bars per loop
  let stepIndex = 0;
  let nextTickAt = ctx.currentTime + 0.1;
  let timerId: number | null = null;

  const scheduleKick = (at: number) => {
    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(110, at);
    osc.frequency.exponentialRampToValueAtTime(45, at + 0.25);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, at);
    g.gain.linearRampToValueAtTime(0.9, at + 0.008);
    g.gain.exponentialRampToValueAtTime(1e-4, at + 0.32);
    osc.connect(g).connect(master);
    osc.start(at);
    osc.stop(at + 0.35);
  };

  const scheduleHat = (at: number, vel: number) => {
    const bufLen = Math.round(ctx.sampleRate * 0.06);
    const buf = ctx.createBuffer(1, bufLen, ctx.sampleRate);
    const ch = buf.getChannelData(0);
    for (let i = 0; i < bufLen; i++) ch[i] = (Math.random() * 2 - 1) * (1 - i / bufLen);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const hp = ctx.createBiquadFilter();
    hp.type = "highpass";
    hp.frequency.value = 6000;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, at);
    g.gain.linearRampToValueAtTime(0.25 * vel, at + 0.002);
    g.gain.exponentialRampToValueAtTime(1e-4, at + 0.07);
    src.connect(hp).connect(g).connect(master);
    src.start(at);
    src.stop(at + 0.08);
  };

  const scheduleBell = (at: number, freq: number) => {
    const osc = ctx.createOscillator();
    osc.type = "triangle";
    osc.frequency.value = freq;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, at);
    g.gain.linearRampToValueAtTime(0.14, at + 0.005);
    g.gain.exponentialRampToValueAtTime(1e-4, at + 0.9);
    // Very short modulation partial for that twinkle.
    const osc2 = ctx.createOscillator();
    osc2.type = "sine";
    osc2.frequency.value = freq * 2.01;
    const g2 = ctx.createGain();
    g2.gain.setValueAtTime(0, at);
    g2.gain.linearRampToValueAtTime(0.06, at + 0.005);
    g2.gain.exponentialRampToValueAtTime(1e-4, at + 0.5);
    osc.connect(g).connect(master);
    osc2.connect(g2).connect(master);
    osc.start(at);
    osc.stop(at + 0.95);
    osc2.start(at);
    osc2.stop(at + 0.55);
  };

  const tick = () => {
    if (!beatsHandle) return;
    // Scheduler lookahead: queue events up to 0.25s in the future,
    // run the tick ~20x/sec. Classic web-audio scheduler pattern.
    const horizon = ctx.currentTime + 0.25;
    while (nextTickAt < horizon) {
      const step = stepIndex % patternLen;
      // Pattern legend (16 8ths, two bars):
      //  0  1  2  3  4  5  6  7   8  9 10 11 12 13 14 15
      //  K  h  .  b  .  h  K  .   .  h  .  b  K  h  .  h
      if (step === 0 || step === 6 || step === 12) scheduleKick(nextTickAt);
      if (step === 1 || step === 5 || step === 9 || step === 13 || step === 15)
        scheduleHat(nextTickAt, 0.8);
      if (step === 3) scheduleBell(nextTickAt, 660);
      if (step === 11) scheduleBell(nextTickAt, 880);
      nextTickAt += stepSec;
      stepIndex++;
    }
    timerId = window.setTimeout(tick, 50);
  };
  timerId = window.setTimeout(tick, 50);

  beatsHandle = {
    stop() {
      const now = ctx.currentTime;
      master.gain.cancelScheduledValues(now);
      master.gain.setValueAtTime(master.gain.value, now);
      master.gain.exponentialRampToValueAtTime(1e-4, now + 0.6);
      if (timerId !== null) window.clearTimeout(timerId);
      timerId = null;
      setTimeout(() => master.disconnect(), 700);
    },
  };
}

export function stopBeats(): void {
  beatsHandle?.stop();
  beatsHandle = null;
}

// ---------- runner music ----------
//
// High-energy procedural loop for Cosmic Runner — driving 4-on-the-floor
// kick + offbeat hat + tight snare + a moving square-wave bass line and a
// soft minor arpeggio on top. Tempo is meant to feel "synthwave chase
// scene" without overpowering the SFX layer. Mute / volume routing flows
// through the shared engine.master so the global mute toggle still works.

let runnerMusicHandle: { stop: () => void } | null = null;

export function startRunnerMusic(): void {
  if (runnerMusicHandle) return;
  const e = ensureEngine();
  if (!e) return;
  const { ctx, comp } = e;

  const master = ctx.createGain();
  master.gain.value = 0;
  master.connect(comp);
  master.gain.setValueAtTime(0, ctx.currentTime);
  // 1.4s ramp-in so the music doesn't smash in at the same instant the
  // mode chooser fades out.
  master.gain.linearRampToValueAtTime(0.075, ctx.currentTime + 1.4);

  const BPM = 148;
  const stepSec = 60 / BPM / 2; // 8th notes
  const patternLen = 16; // two bars
  let stepIndex = 0;
  let nextTickAt = ctx.currentTime + 0.1;
  let timerId: number | null = null;

  // ---- voices ----
  const scheduleKick = (at: number) => {
    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(140, at);
    osc.frequency.exponentialRampToValueAtTime(50, at + 0.18);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, at);
    g.gain.linearRampToValueAtTime(1.0, at + 0.005);
    g.gain.exponentialRampToValueAtTime(1e-4, at + 0.26);
    osc.connect(g).connect(master);
    osc.start(at);
    osc.stop(at + 0.3);
  };

  const scheduleSnare = (at: number) => {
    // Body — short pitched click for the snap.
    const body = ctx.createOscillator();
    body.type = "triangle";
    body.frequency.setValueAtTime(220, at);
    body.frequency.exponentialRampToValueAtTime(110, at + 0.07);
    const bodyG = ctx.createGain();
    bodyG.gain.setValueAtTime(0, at);
    bodyG.gain.linearRampToValueAtTime(0.45, at + 0.005);
    bodyG.gain.exponentialRampToValueAtTime(1e-4, at + 0.12);
    body.connect(bodyG).connect(master);
    body.start(at);
    body.stop(at + 0.14);
    // Noise — the actual snare wires.
    const bufLen = Math.round(ctx.sampleRate * 0.18);
    const buf = ctx.createBuffer(1, bufLen, ctx.sampleRate);
    const ch = buf.getChannelData(0);
    for (let i = 0; i < bufLen; i++) ch[i] = (Math.random() * 2 - 1) * (1 - i / bufLen);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const bp = ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.frequency.value = 1800;
    bp.Q.value = 0.7;
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(0, at);
    ng.gain.linearRampToValueAtTime(0.55, at + 0.003);
    ng.gain.exponentialRampToValueAtTime(1e-4, at + 0.18);
    src.connect(bp).connect(ng).connect(master);
    src.start(at);
    src.stop(at + 0.2);
  };

  const scheduleHat = (at: number, vel: number, open = false) => {
    const dur = open ? 0.16 : 0.05;
    const bufLen = Math.round(ctx.sampleRate * dur);
    const buf = ctx.createBuffer(1, bufLen, ctx.sampleRate);
    const ch = buf.getChannelData(0);
    for (let i = 0; i < bufLen; i++) ch[i] = (Math.random() * 2 - 1) * (1 - i / bufLen);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const hp = ctx.createBiquadFilter();
    hp.type = "highpass";
    hp.frequency.value = 7000;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, at);
    g.gain.linearRampToValueAtTime(0.22 * vel, at + 0.002);
    g.gain.exponentialRampToValueAtTime(1e-4, at + dur);
    src.connect(hp).connect(g).connect(master);
    src.start(at);
    src.stop(at + dur + 0.02);
  };

  const scheduleBass = (at: number, freq: number) => {
    // Square + lowpass = chunky synth bass. Pluck envelope on the
    // filter cutoff so each note has bite without sounding like a saw.
    const osc = ctx.createOscillator();
    osc.type = "square";
    osc.frequency.value = freq;
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.Q.value = 5;
    lp.frequency.setValueAtTime(1400, at);
    lp.frequency.exponentialRampToValueAtTime(280, at + 0.18);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, at);
    g.gain.linearRampToValueAtTime(0.32, at + 0.005);
    g.gain.exponentialRampToValueAtTime(1e-4, at + 0.22);
    osc.connect(lp).connect(g).connect(master);
    osc.start(at);
    osc.stop(at + 0.24);
  };

  const scheduleArp = (at: number, freq: number) => {
    // Soft triangle pluck — sits well above the bass without colliding
    // with the kick.
    const osc = ctx.createOscillator();
    osc.type = "triangle";
    osc.frequency.value = freq;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, at);
    g.gain.linearRampToValueAtTime(0.085, at + 0.008);
    g.gain.exponentialRampToValueAtTime(1e-4, at + 0.35);
    osc.connect(g).connect(master);
    osc.start(at);
    osc.stop(at + 0.4);
  };

  // ---- pattern ----
  // Two-bar loop. Notes in C minor — root C2 (~65 Hz) up to a triadic
  // arpeggio one octave higher.
  const C2 = 65.41;
  const G2 = 98.0;
  const C3 = 130.81;
  const Eb3 = 155.56;
  const G3 = 196.0;
  const C4 = 261.63;
  // Bass riff per 8th (16 steps, 2 bars). null = rest.
  const bassPattern: (number | null)[] = [
    C2, null, C2,  null, G2, null, C3,  null,
    C2, null, G2,  null, C3, null, Eb3, null,
  ];
  // Arp pattern — sparse but driving.
  const arpPattern: (number | null)[] = [
    null, G3,   null, C4,  null, Eb3, null, G3,
    null, Eb3,  null, G3,  null, C4,  null, Eb3,
  ];

  const tick = () => {
    if (!runnerMusicHandle) return;
    const horizon = ctx.currentTime + 0.3;
    while (nextTickAt < horizon) {
      const step = stepIndex % patternLen;
      // 4-on-the-floor: kick on every quarter note (steps 0, 2, 4, …).
      if (step % 2 === 0) scheduleKick(nextTickAt);
      // Snare on beats 2 + 4 of each bar (steps 4, 12).
      if (step === 4 || step === 12) scheduleSnare(nextTickAt);
      // Closed hats every 8th, open hat near end of each bar for lift.
      const open = step === 7 || step === 15;
      scheduleHat(nextTickAt, open ? 0.6 : 0.5, open);
      const bn = bassPattern[step];
      if (bn !== null && bn !== undefined) scheduleBass(nextTickAt, bn);
      const an = arpPattern[step];
      if (an !== null && an !== undefined) scheduleArp(nextTickAt, an);
      nextTickAt += stepSec;
      stepIndex++;
    }
    timerId = window.setTimeout(tick, 60);
  };
  timerId = window.setTimeout(tick, 60);

  runnerMusicHandle = {
    stop() {
      const now = ctx.currentTime;
      master.gain.cancelScheduledValues(now);
      master.gain.setValueAtTime(master.gain.value, now);
      master.gain.exponentialRampToValueAtTime(1e-4, now + 0.5);
      if (timerId !== null) window.clearTimeout(timerId);
      timerId = null;
      // Disconnect after the fade-out is audible.
      setTimeout(() => master.disconnect(), 700);
    },
  };
}

export function stopRunnerMusic(): void {
  runnerMusicHandle?.stop();
  runnerMusicHandle = null;
}
